if (!customElements.get('subscription-gift')) {
  customElements.define('subscription-gift', class SubscriptionGift extends HTMLElement {
    get planName() {
      return (this.getAttribute('data-plan-name') || '').trim().toLowerCase();
    }

    get planNameRaw() {
      return this.getAttribute('data-plan-name') || '';
    }

    get giftVariantId() {
      return String(this.getAttribute('data-gift-variant-id') || '');
    }

    get giftStock() {
      let v = this.getAttribute('data-gift-stock');
      if (v === null || v === '') return Infinity;
      let n = parseInt(v, 10);
      return isNaN(n) ? Infinity : n;
    }

    connectedCallback() {
      if (!this.planName || !this.giftVariantId) return;
      if (!window.FoxTheme || !FoxTheme.pubsub || !FoxTheme.routes) return;
      this._busy = false;
      this._pending = false;
      this._target = null;
      this._maxAchievable = Infinity;
      this._onCartOpStart = () => this.lockCheckout();
      document.addEventListener('cart:grouped-sections', this._onCartOpStart);
      this._unsubscribe = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (this._busy) {
          this._pending = true;
          this.lockCheckout();
          return;
        }
        if (event && event.cart && event.cart.items) {
          this.reconcile(event.cart);
        } else {
          this.fetchAndReconcile();
        }
      });
      this.fetchAndReconcile();
    }

    disconnectedCallback() {
      if (this._unsubscribe) this._unsubscribe();
      document.removeEventListener('cart:grouped-sections', this._onCartOpStart);
      this.unlockCheckout();
    }

    fetchAndReconcile() {
      fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })
        .then((response) => response.json())
        .then((cart) => this.reconcile(cart))
        .catch(() => {});
    }

    isGiftLine(line) {
      let props = line && line.properties;
      return !!props && (props._gift === 'true' || props._gift === true);
    }

    qualifyingQuantity(cart) {
      let total = 0;
      cart.items.forEach((line) => {
        if (this.isGiftLine(line)) return;
        let allocation = line.selling_plan_allocation;
        let plan = allocation && allocation.selling_plan;
        let name = plan && plan.name ? String(plan.name).trim().toLowerCase() : '';
        if (name !== '' && name === this.planName) total += line.quantity;
      });
      return total;
    }

    reconcile(cart) {
      if (this._busy || !cart || !cart.items) return;
      let qualifying = this.qualifyingQuantity(cart);
      if (qualifying === 0) this._maxAchievable = Infinity;
      let desired = Math.min(qualifying, 1, this._maxAchievable);
      if (desired < 0) desired = 0;

      let giftLines = cart.items.filter((line) => this.isGiftLine(line));
      let current = giftLines.reduce((total, line) => total + line.quantity, 0);

      if (desired > current) {
        desired = Math.min(desired, Math.max(current, this.giftStock));
      }

      if (desired === current && giftLines.length <= 1) {
        this.unlockCheckout();
        return;
      }

      this.lockCheckout();
      if (giftLines.length === 0) {
        if (desired > 0) this.mutate(this.addBody(desired), desired, true);
        else this.unlockCheckout();
        return;
      }

      let updates = {};
      giftLines.forEach((line, i) => {
        updates[line.key] = i === 0 ? desired : 0;
      });
      this.mutate(this.updateBody(updates), desired);
    }

    addBody(quantity) {
      return {
        url: FoxTheme.routes.cart_add_url,
        config: FoxTheme.utils.fetchConfig('javascript'),
        body: {
          id: this.giftVariantId,
          quantity: quantity,
          properties: { _gift: 'true', _gift_for_plan: this.planNameRaw },
          sections: this.gatherSections(),
          sections_url: window.location.pathname,
        },
      };
    }

    updateBody(updates) {
      return {
        url: FoxTheme.routes.cart_update_url,
        config: FoxTheme.utils.fetchConfig(),
        body: {
          updates: updates,
          sections: this.gatherSections(),
          sections_url: window.location.pathname,
        },
      };
    }

    gatherSections() {
      let sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
      );
      return sections;
    }

    mutate(request, target, isAdd) {
      this._busy = true;
      this._target = target;
      let sections = request.body.sections;
      fetch(`${request.url}`, { ...request.config, ...{ body: JSON.stringify(request.body) } })
        .then((response) => response.json())
        .then(async (result) => {
          let cart = result;
          if (isAdd || !cart || !cart.items) {
            cart = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
            if (result && result.sections) cart.sections = result.sections;
          }
          this.afterMutation(cart);
        })
        .catch(() => {
          this._busy = false;
          this.unlockCheckout();
          this.flushPending();
        });
    }

    lockCheckout() {
      document.body.classList.add('gift-reconciling');
      this.setQuantityControlsDisabled(true);
      queueMicrotask(() => {
        if (document.body.classList.contains('gift-reconciling')) this.setQuantityControlsDisabled(true);
      });
    }

    unlockCheckout() {
      document.body.classList.remove('gift-reconciling');
      this.setQuantityControlsDisabled(false);
    }

    setQuantityControlsDisabled(disabled) {
      document.querySelectorAll('.cart-quantity .quantity__button, .cart-quantity .quantity__input').forEach((el) => {
        if (disabled) {
          if (!el.disabled) {
            el.disabled = true;
            el.setAttribute('data-gift-lock', '');
          }
        } else if (el.hasAttribute('data-gift-lock')) {
          el.disabled = false;
          el.removeAttribute('data-gift-lock');
        }
      });
    }

    afterMutation(cart) {
      let achieved = 0;
      if (cart && cart.items) {
        achieved = cart.items.reduce((total, line) => {
          return this.isGiftLine(line) ? total + line.quantity : total;
        }, 0);
      }
      if (typeof this._target === 'number' && this._target > 0 && achieved < this._target) {
        this._maxAchievable = achieved;
      }
      if (cart && !cart.sections) cart.sections = {};
      this._busy = false;
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
      this.flushPending();
    }

    flushPending() {
      if (this._pending && !this._busy) {
        this._pending = false;
        this.fetchAndReconcile();
      }
    }
  });
}
